/* @flow */
import * as React from 'react';
import ReactTooltip from 'react-tooltip';
import classnames from 'classnames';
import {colors} from '../colors';
import {GoalLeadInfoIcon, WarningIcon, ErrorCircleIcon, ConfirmIcon, LockIcon} from '../icon';

import './status-badge.css';

export type Level =
    | 'info'
    | 'info--purple'
    | 'info--navy'
    | 'info--grey'
    | 'warning'
    | 'alert'
    | 'success'
    | 'hidden';

type Props = {
    level: Level,
    text: string,
    detail?: string,
    includeIcon?: boolean,
    isInteractive?: boolean,
    size?: 'small' | 'big',
};

export function StatusBadge(props: Props) {
    const {includeIcon, level, detail, text, size, isInteractive} = props;

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, []);

    const statusStyleName = classnames({
        'status-badge': true,
        'status-badge--small': size === 'small',
        'status-badge--interactive': isInteractive,
        'status-badge--warning': level === 'warning',
        'status-badge--alert': level === 'alert',
        'status-badge--info': level === 'info',
        'status-badge--success': level === 'success',
        'status-badge--hidden': level === 'hidden',
        'status-badge--info-purple': level === 'info--purple',
        'status-badge--info-navy': level === 'info--navy',
        'status-badge--info-grey': level === 'info--grey',
    });

    let icon = null;

    if (includeIcon) {
        const Icon = getIcon(level);
        icon = <Icon wrapWithDiv={false} size={15} fill={getFillColor(level)} />;
    }

    return (
        <div
            styleName={statusStyleName}
            tabIndex={detail || isInteractive ? 0 : -1}
            data-tip={detail}
            data-effect='solid'
            data-delay-show='2'
            data-event='mouseenter focus'
            data-event-off='mouseleave blur click'
        >
            {icon}
            <span>{text}</span>
        </div>
    );
}

export function getFillColor(level: Level) {
    switch (level) {
        case 'info':
            return 'hsla(195, 71%, 37%, 1)';
        case 'warning':
            return 'hsla(20, 81%, 56%, 1)';
        case 'alert':
            return 'hsla(360, 95%, 64%, 1)';
        case 'success':
            return 'hsla(145, 71%, 26%, 1)';
        case 'hidden':
            return colors.greyDk;
        default:
            return colors.grey;
    }
}

function getIcon(level: Level) {
    switch (level) {
        case 'info':
            return GoalLeadInfoIcon;
        case 'warning':
            return WarningIcon;
        case 'alert':
            return ErrorCircleIcon;
        case 'success':
            return ConfirmIcon;
        case 'hidden':
            return LockIcon;
        default:
            return GoalLeadInfoIcon;
    }
}
